<template>
  <el-dialog
    class="form-modal"
    :visible.sync="visible"
    v-loading="processing"
    :fullscreen="true"
    :show-close="false"
    :append-to-body="true"
  >
    <div class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">{{ modalTitle }}</h1>
        <div class="modal-actions col-2">
          <el-button
            v-if="activeStep !== 2"
            type="primary"
            @click="handleSubmit"
            >{{ activeStep === 0 ? context : "Save Changes" }}</el-button
          >
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            {{ activeStep === 2 ? "Close" : "Cancel" }}</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          />
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits
        v-if="context === 'Edit'"
        service="Business Hour"
        :record="currentRecord"
      />
      <el-steps :active="activeStep" finish-status="success">
        <el-step
          :style="{ cursor: context === 'Edit' ? 'pointer' : 'default' }"
          @click.native="goToStep(0)"
        ></el-step>
        <el-step
          :style="{ cursor: context === 'Edit' ? 'pointer' : 'default' }"
          @click.native="goToStep(1)"
        ></el-step>
        <el-step
          :style="{ cursor: context === 'Edit' ? 'pointer' : 'default' }"
          @click.native="goToStep(2)"
        ></el-step>
      </el-steps>

      <div v-if="context === 'Edit'" class="button-group mb-20">
        <div class="flex">
          <el-button
            v-if="activeStep > 0"
            icon="el-icon-arrow-left"
            type="default"
            @click="stepBack"
            >{{ stepBackBtnLabel }}
          </el-button>

          <div class="box right">
            <el-button
              v-if="stepForwardBtnLabel"
              type="default"
              @click="stepForward"
              >{{ stepForwardBtnLabel }}
              <i class="el-icon-arrow-right el-icon-right"></i
            ></el-button>
          </div>
        </div>
      </div>

      <div id="step-1" v-if="activeStep == 0">
        <el-input v-model="form.name" placeholder="Name">
          <template slot="prefix">Name</template>
        </el-input>

        <dads-select
          placeholder="Select branch"
          v-model="form.branchId"
          filterable
        >
          <template slot="prefix"> Branch </template>
          <el-option
            v-for="item in branchOptions"
            :key="item.id"
            :value="item.id"
            :label="item.name"
          />
        </dads-select>

        <dads-select
          v-model="form.timezone"
          placeholder="Select a time zone"
          allow-create
          filterable
        >
          <template slot="prefix">Time zone</template>
          <el-option label="Use account default" value="" />
          <el-option
            v-for="(timezone, index) in timezones"
            :key="index"
            :label="timezone.name"
            :value="timezone.name"
          />
        </dads-select>

        <h2 class="black-text form-section-header">Regular hours</h2>
        <el-tag class="mb-10 mt-5" size="medium">
          <i class="el-icon-info" style="margin-right: 4px"></i>
          Set your standard operating hours
        </el-tag>

        <div
          class="inline-form-group align-center black-text"
          v-for="(day, index) in days"
          :key="index"
        >
          <div
            class="flex align-center"
            style="align-self: flex-start; margin-top: 22px"
          >
            <h4 class="mt-0" style="width: 95px">{{ day }}</h4>
            <el-switch
              :value="hours[index].open"
              @change="onOpenToggle(index, $event)"
            />
            <span class="ml-5" style="width: 60px">
              {{ hours[index].open ? "Open" : "Closed" }}
            </span>
          </div>

          <multi-time-range-selector
            :values="hours[index].values"
            :disabled="!hours[index].open"
            @timeRangeSelected="onTimeRangeSelection(index, $event)"
          />
        </div>
      </div>

      <div id="step-2" v-if="activeStep == 1">
        <h2 class="form-section-header">During Business Hours</h2>
        <destination-selector
          title=""
          :draggable="true"
          :showHeader="false"
          :clearFields="clearFields"
          :draggableDestTypes="draggableDestTypes"
          :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
          :selectedDestinations="insideHoursDestinations"
          @destinationsSelected="onInsideDestinationsSelection"
        />

        <h2 class="form-section-header">Outside Business Hours</h2>
        <destination-selector
          title=""
          :draggable="true"
          :showHeader="false"
          :clearFields="clearFields"
          :draggableDestTypes="draggableDestTypes"
          :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
          :selectedDestinations="outsideHoursDestinations"
          @destinationsSelected="onOutsideDestinationsSelection"
        />
      </div>

      <div id="step-3" v-show="activeStep == 2">
        <holidays-list :businessHoursId="recordId" />
      </div>
    </el-form>

    <destination-references
      v-if="context === 'Edit'"
      type="CONDITION"
      :clearState="clearFields"
      :id="currentRecord ? currentRecord.id : null"
      :tenantId="currentRecord ? currentRecord.tenantId : null"
    />
  </el-dialog>
</template>

<script>
import {
  getBusinessHour,
  editBusinessHour,
  createBusinessHour
} from "@/api/business-hours/methods.js";
import DadsSelect from "@/components/select";
import HolidaysList from "./holidaysList.vue";
import { getBranches } from "@/api/branches/methods.js";
import RecentAudits from "@/components/RecentAudits.vue";
import MultiTimeRangeSelector from "./multiTimeRangeSelector.vue";
import DestinationSelector from "@/components/DestinationSelector.vue";
import DestinationReferences from "@/components/DestinationReferences.vue";
import { mapDestinationFields } from "@/globals/globals.js";

export default {
  name: "BusinessHoursForm",

  props: {
    context: { required: true },
    visible: { required: true },
    timezones: { required: true },
    selectedRecordId: { required: true },
    draggableDestTypes: { required: true }
  },

  components: {
    DadsSelect,
    RecentAudits,
    HolidaysList,
    DestinationSelector,
    DestinationReferences,
    MultiTimeRangeSelector
  },

  data() {
    return {
      clearFields: false,
      recordId: null,
      currentRecord: null,
      activeStep: 0,
      processing: false,
      days: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      hours: {
        0: {
          open: false,
          values: []
        },
        1: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        2: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        3: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        4: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        5: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        6: {
          open: false,
          values: []
        }
      },

      insideHoursDestinations: [],
      outsideHoursDestinations: [],

      form: {
        name: "",
        timezone: "",
        branchId: null
      },

      branchOptions: []
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    modalTitle() {
      if (this.activeStep === 0) {
        return `${
          this.context === "Create" ? this.context : "View / Edit"
        } Business Hour`;
      }

      if (this.activeStep === 1) {
        return `${
          this.context === "Create" ? "Configure" : "View / Configure"
        } Routing`;
      }

      return `${
        this.context === "Create" ? "Configure" : "View / Configure"
      } Exceptions`;
    },

    stepForwardBtnLabel() {
      if (this.activeStep === 0) {
        return "Routing Config";
      }

      if (this.activeStep === 1) {
        return "Special Holidays";
      }

      return null;
    },

    stepBackBtnLabel() {
      if (this.activeStep === 1) {
        return "General Settings";
      }

      if (this.activeStep === 2) {
        return "Routing Config";
      }
    }
  },

  watch: {
    visible() {
      if (this.visible) {
        this.clearFields = false;
      }
    },

    selectedTenant() {
      this.queryBranches();
    },

    selectedRecordId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.recordId = null;
          this.recordId = id;
          this.processing = true;
          getBusinessHour({ id }).then(({ success, data }) => {
            this.processing = false;
            if (success) {
              this.currentRecord = data;
            }
          });
        }
      }
    },

    currentRecord(record) {
      if (record) {
        const {
          name,
          timezone,
          branch,
          matchDestinations,
          notMatchDestinations
        } = record;

        this.form.name = name;
        this.form.timezone = timezone;
        this.form.branchId = branch.id;

        for (const [key, blocks] of Object.entries(record.hours)) {
          if (blocks && Array.isArray(blocks)) {
            this.hours[key].open = blocks.length > 0;
            this.hours[key].values = blocks;
          }
        }

        if (matchDestinations.length) {
          this.insideHoursDestinations = matchDestinations
            .sort((a, b) => b.order - a.order)
            .map((destination) => ({
              id: destination.id,
              type: destination.destType,
              options: [mapDestinationFields(destination)]
            }));
        }

        if (notMatchDestinations.length) {
          this.outsideHoursDestinations = notMatchDestinations
            .sort((a, b) => b.order - a.order)
            .map((destination) => ({
              id: destination.id,
              type: destination.destType,
              options: [mapDestinationFields(destination)]
            }));
        }
      }
    }
  },

  methods: {
    stepBack() {
      this.activeStep--;
    },

    stepForward() {
      this.activeStep++;
    },

    goToStep(step) {
      if (this.context === "Edit") {
        this.activeStep = step;
      }
    },

    queryBranches() {
      getBranches({ tenantId: this.selectedTenant.id }).then(
        ({ success, data }) => {
          if (success) {
            const { records } = data;
            this.branchOptions = records;
            this.form.branchId = this.branchOptions.find(
              (br) => br.default === 1
            ).id;
          }
        }
      );
    },

    onOpenToggle(dayIndex, open) {
      this.hours[dayIndex].open = open;

      if (open && !this.hours[dayIndex].values.length) {
        this.hours[dayIndex].values = [
          {
            endTime: "17:00",
            startTime: "09:00"
          }
        ];
      }
    },

    onTimeRangeSelection(index, ranges) {
      this.hours[index].values = [...ranges];
    },

    onInsideDestinationsSelection(destinations) {
      this.insideHoursDestinations = [...destinations];
    },

    onOutsideDestinationsSelection(destinations) {
      this.outsideHoursDestinations = [...destinations];
    },

    validateDestinations({ destinations = [], type }) {
      if (
        destinations.length === 0 ||
        (destinations.length === 1 && destinations[0].id === null)
      ) {
        return [];
      }

      const emptyDst = destinations.find((dst) => !dst.id && dst.id !== 0);

      if (emptyDst) {
        this.$notify({
          title: "Empty Destination",
          message: `${type} destination can't be empty.`,
          type: "error"
        });
        return null;
      }

      return destinations.map(({ id, type }) => ({ id, type }));
    },

    validateHours() {
      const hours = {};
      for (const [key, { open, values }] of Object.entries(this.hours)) {
        if (open && values.length === 0) {
          this.$notify({
            title: "Bad Request",
            type: "error",
            message: `Missing time block for ${this.days[key]}`
          });
          return null;
        }

        hours[key] = open ? values : [];
      }

      return hours;
    },

    async createOperation() {
      try {
        await this.$confirm("Create Business Hour?", "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        const hours = this.validateHours();

        if (!hours) {
          return;
        }

        this.processing = true;
        const { name, branchId, timezone } = this.form;
        const body = {
          name,
          hours,
          branchId,
          tenantId: this.selectedTenant.id,
          // Assign Account Default Timezone
          timezone: timezone ? timezone : this.selectedTenant.timezone.name
        };

        const { success, data } = await createBusinessHour({ body });
        this.processing = false;

        if (success) {
          this.recordId = data.id;
          this.activeStep++;
        }
      } catch (err) {
        this.processing = false;
      }
    },

    async editOperation() {
      const title =
        this.activeStep === 0
          ? "Edit this Business Hour?"
          : "Configure Business Hour Routing?";

      try {
        await this.$confirm(title, "Warning", {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel"
        });

        const hours = this.validateHours();

        if (!hours) {
          return;
        }

        const { name, branchId, timezone } = this.form;
        let body = {
          name,
          hours,
          branchId,
          timezone: timezone ? timezone : this.selectedTenant.timezone.name
        };

        if (this.activeStep >= 1) {
          const insideHoursDestinations = this.validateDestinations({
            destinations: this.insideHoursDestinations,
            type: "During Business Hour"
          });

          const outsideHoursDestinations = this.validateDestinations({
            destinations: this.outsideHoursDestinations,
            type: "Outside Business Hour"
          });

          if (!insideHoursDestinations || !outsideHoursDestinations) {
            return;
          }

          body = {
            ...body,
            insideHoursDestinations,
            outsideHoursDestinations
          };
        }

        this.processing = true;

        const { success } = await editBusinessHour({
          id: this.recordId,
          body,
          ...(this.activeStep === 1
            ? { successMessage: "Routing configured successfully!" }
            : {})
        });

        this.processing = false;

        if (success) {
          this.activeStep++;
        }
      } catch (err) {
        this.processing = false;
      }
    },

    cancelOperation() {
      this.clearForm();
      this.$emit("cancelOperation");
    },

    handleSubmit() {
      if (this.activeStep === 0 && this.context === "Create") {
        this.createOperation();
        return;
      }

      if (this.activeStep !== 2) {
        this.editOperation();
      }
    },

    clearForm() {
      const defaultBranch = this.branchOptions.find((br) => br.default === 1);
      this.clearFields = true;
      this.activeStep = 0;
      this.recordId = null;
      this.currentRecord = null;
      this.hours = {
        0: {
          open: false,
          values: []
        },
        1: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        2: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        3: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        4: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        5: {
          open: true,
          values: [{ startTime: "09:00", endTime: "17:00" }]
        },
        6: {
          open: false,
          values: []
        }
      };

      this.insideHoursDestinations = [];
      this.outsideHoursDestinations = [];

      this.form = {
        name: "",
        timezone: "",
        branchId: defaultBranch.id
      };
    }
  },

  created() {
    this.queryBranches();
  }
};
</script>

<style lang="scss" scoped>
.el-form {
  max-width: 600px;
  margin: 35px auto 0 auto;

  h4 {
    margin-bottom: 0;
  }

  .el-steps {
    width: 150px;
    margin: auto;
    margin-bottom: 30px;
  }
}
</style>
